

import React, { useState, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calculator, FileText, TrendingUp, Download, Settings, Sparkles, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useNavigate } from 'react-router-dom';
import { ReportFormatSelector } from './unified/ReportFormatSelector';
import CalculatorTabsContent from './tabs/TabsContent';
import { CalculatorReportEmailComposer } from '@/features/email/components/CalculatorReportEmailComposer';

import { EnhancedResultsDisplay } from './EnhancedResultsDisplay';
import { AnalyticsDashboard } from './advanced/AnalyticsDashboard';
import { SupremeIntelligenceTab } from './supreme-intelligence/SupremeIntelligenceTab';

import { useUnifiedCalculator } from '@/hooks/useUnifiedCalculator';
import { useDataCapture } from '@/hooks/useDataCapture';
import { useCalculatorDraft } from '@/hooks/drafts/useCalculatorDraft';
import { useMultiFormatReportGeneration } from '@/hooks/useMultiFormatReportGeneration';
import { transformOptionsToStates, transformOptionsToCities } from './utils/dataTransformers';
import { transformMortgageDataToReportData } from '@/utils/calculator/reportDataTransformer';
import { transformMortgageDataToCalculatorReport } from '@/utils/calculator/calculatorReportTransformer';
import { transformFromMortgageData } from '@/utils/unified/dataTransformers';
import { useLocationData } from './tabs/useLocationData';
import { AutoCalculateOptions } from './utils/types';
import { EducationalPanel } from './education/EducationalPanel';
import { FactorPaymentsChart } from './FactorPaymentsChart';

export const IntegratedCalculatorContent = ({ calculator, draft, autoOptions }: { autoOptions: [AutoCalculateOptions, React.Dispatch<React.SetStateAction<AutoCalculateOptions>>]; calculator: ReturnType<typeof useUnifiedCalculator>; draft: ReturnType<typeof useCalculatorDraft> }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calculator');
  const [buydownScenarios, setBuydownScenarios] = useState<any[]>([]);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  // Add proper autoCalculate state management
  const [autoCalculate, setAutoCalculate] = autoOptions;

  // Use unified calculator hook
  const { mortgageData, handlers, uiState, setMortgageData } = calculator;
  const { captureMortgageCalculation } = useDataCapture();

  // Enable draft persistence across sessions
  const { isSaving, lastSaved, isLoading } = draft;

  // Use multi-format report generation hook
  const { generateReport, isGenerating } = useMultiFormatReportGeneration();

  // Calculate completion status
  const hasCalculated = mortgageData.monthlyPayment.total > 0;
  const isCalculationComplete = mortgageData.monthlyPayment.total > 0 && mortgageData.closingCosts.total > 0;

  // Capture anonymized data when calculation completes
  React.useEffect(() => {
    if (isCalculationComplete) {
      captureMortgageCalculation(mortgageData);
    }
  }, [isCalculationComplete, mortgageData.homePrice, mortgageData.loanType, mortgageData.interestRate, mortgageData.loanTerm, mortgageData.downPayment]);

  // Get location data and transform to expected format
  const locationData = useLocationData(mortgageData);
  
  const states = transformOptionsToStates(locationData.states?.map(s => ({ 
    value: s.abbreviation, 
    label: s.name 
  })) || []);
  
  const cities = transformOptionsToCities(locationData.cities?.map(c => ({ 
    value: c.id, 
    label: c.name 
  })) || []);

  // Transform to form data for Supreme Intelligence features
  const formData = transformFromMortgageData(mortgageData);

  // Handle report generation
  const handleGenerateReport = async (format: 'txt' | 'csv' | 'json' | 'html' | 'xlsx') => {
    const reportData = transformMortgageDataToReportData(mortgageData, user);
    await generateReport(format, reportData);
  };

  // Handle professional report navigation
  const handleViewProfessionalReport = () => {
    const reportData = transformMortgageDataToCalculatorReport(mortgageData, user);
    navigate('/calculator-report', { state: { reportData } });
  };

  return (
    <div className="w-full space-y-6">
      {/* Header with Generate Report Button */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Mortgage Calculator</h2>
          <div className="flex items-center gap-3">
            <p className="text-muted-foreground">Calculate your monthly payments and closing costs</p>
            {isSaving && (
              <span className="text-xs text-muted-foreground animate-pulse">Saving...</span>
            )}
            {!isSaving && lastSaved && (
              <span className="text-xs text-muted-foreground">
                Saved {new Date(lastSaved).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isCalculationComplete && (
            <>
              <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Report
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-5xl max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle>Send Professional Calculator Report via Email</DialogTitle>
                  </DialogHeader>
                  <CalculatorReportEmailComposer
                    mortgageData={mortgageData}
                    buydownScenarios={buydownScenarios}
                    formData={formData}
                    onSuccess={() => setIsEmailDialogOpen(false)}
                    onCancel={() => setIsEmailDialogOpen(false)}
                  />
                </DialogContent>
              </Dialog>
              <Button
                onClick={handleViewProfessionalReport}
                variant="default"
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                View Professional Report
              </Button>
              <ReportFormatSelector
                onGenerateReport={handleGenerateReport}
                isGenerating={isGenerating}
                disabled={!isCalculationComplete}
              />
            </>
          )}
          
        </div>
      </div>


      {/* Calculation Status Indicator */}
      {!hasCalculated && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm">
            💡 Enter your mortgage details below. Calculations will run automatically as you input data.
          </p>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-2 xl:grid-cols-4">
          <TabsTrigger value="calculator" className="flex min-h-11 items-center gap-2">
            <Calculator className="w-4 h-4" />
            Calculator
          </TabsTrigger>
          <TabsTrigger value="results" className="flex min-h-11 items-center gap-2">
            <FileText className="w-4 h-4" />
            Results
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex min-h-11 items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="supreme-intelligence" className="flex min-h-11 items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Supreme Intelligence
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calculator" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Mortgage Calculator
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CalculatorTabsContent
                mortgageData={mortgageData}
                setMortgageData={setMortgageData}
                autoCalculate={autoCalculate}
                setAutoCalculate={setAutoCalculate}
                states={states}
                cities={cities}
                calculatorHandlers={handlers}
              />
            </CardContent>
          </Card>


          {/* Results Button */}
          {hasCalculated && (
            <div className="flex justify-center pt-6">
              <Button
                onClick={() => setActiveTab('results')}
                size="lg"
                className="px-8 py-3 text-lg"
              >
                <TrendingUp className="w-5 h-5 mr-2" />
                View Results
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="results" className="space-y-6">
          <EnhancedResultsDisplay 
            mortgageData={mortgageData}
            onBuydownScenariosCalculated={setBuydownScenarios}
            onHomePriceChange={handlers.handleHomePriceChange}
          />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <AnalyticsDashboard 
            mortgageData={mortgageData}
            buydownScenarios={buydownScenarios}
          />
        </TabsContent>

        <TabsContent value="supreme-intelligence" className="space-y-6">
          <SupremeIntelligenceTab 
            mortgageData={mortgageData} 
            formData={formData}
          />
        </TabsContent>
      </Tabs>

      {/* Factor Payments Reference Chart */}
      <FactorPaymentsChart defaultExpanded={false} />
    </div>
  );
};

export const IntegratedCalculatorInterface = () => {
  const calculator = useUnifiedCalculator();
  const autoOptions = useState<AutoCalculateOptions>({ propertyTax: true, insurance: true, pmi: true, closingCosts: true });
  const draft = useCalculatorDraft({ mortgageData: calculator.mortgageData, setMortgageData: calculator.setMortgageData, step: 'main' });
  return <IntegratedCalculatorContent calculator={calculator} draft={draft} autoOptions={autoOptions} />;
};
